import { useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import { Button, Icon } from './components/ui';
import AuthLayout, { AuthAlert, AuthField, AuthHead, EMAIL_RE, homeFor, readJson } from './components/auth/AuthLayout';
import './SignUpPage.css';

const KINDS = [
    { value: 'player', label: 'Player', sub: 'I play therapy games' },
    { value: 'provider', label: 'Provider', sub: 'I sponsor and review players' },
];

const RULES = [
    { label: '8+ characters', test: (pw) => pw.length >= 8 },
    { label: 'A number', test: (pw) => /\d/.test(pw) },
    { label: 'A letter', test: (pw) => /[a-z]/i.test(pw) },
];

/** "I am a" — two-option radiogroup with a caption under each label (arrow keys move the choice). */
function AccountType({ value, onChange }) {
    const ref = useRef(null);
    const onKey = (e) => {
        const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        const i = (KINDS.findIndex((k) => k.value === value) + d + KINDS.length) % KINDS.length;
        onChange(KINDS[i].value);
        ref.current?.querySelectorAll('[role=radio]')[i]?.focus();
    };
    return (
        <div className="sp-kind">
            <span className="eg-field__label" id="sp-kind-label">I am a</span>
            <div ref={ref} role="radiogroup" aria-labelledby="sp-kind-label" className="sp-kind__group" onKeyDown={onKey}>
                {KINDS.map((k) => {
                    const on = k.value === value;
                    return (
                        <button key={k.value} type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1} className="sp-kind__opt" onClick={() => onChange(k.value)}>
                            <span className="sp-kind__label">{k.label}</span>
                            <span className="sp-kind__sub">{k.sub}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/** Map a failed /auth/register response to a field error or a form-level alert. */
function registerProblem(status, data) {
    const message = data.message || data.error || '';
    if (status === 429 || /too many/i.test(message)) {
        return { alert: { title: 'Too many attempts.', text: 'Please try again later.' } };
    }
    // with PROVIDER_SIGNUP_KEY set, the beta key alone does not make a provider: the provider key goes with it
    if (/provider key/i.test(message)) return { field: { providerKey: 'Enter the provider key.' }, providerKey: true };
    if (/beta key/i.test(message)) return { field: { key: 'That key isn’t recognised.' } };
    if (/already in use/i.test(message)) {
        return {
            field: { email: 'An account already uses this email.' },
            alert: data.verified === false
                ? { title: 'This email is registered but not verified.', verify: true }
                : { title: 'This email already has an account.', login: true },
        };
    }
    if (/do not match/i.test(message)) return { field: { confirm: 'Passwords don’t match.' } };
    return { alert: { title: 'We couldn’t create your account.', text: message || 'Please try again.' } };
}

function SignUpPage() {
    const { isAuthenticated, isInitialized, user } = useAuth();
    const navigate = useNavigate();
    const [accountType, setAccountType] = useState('player');
    const [v, setV] = useState({ name: '', email: '', password: '', confirm: '', key: '', providerKey: '' });
    const [tried, setTried] = useState(false);
    const [busy, setBusy] = useState(false);
    const [server, setServer] = useState({ field: {}, alert: null });
    const [askProviderKey, setAskProviderKey] = useState(false);
    const refs = { name: useRef(null), email: useRef(null), password: useRef(null), confirm: useRef(null), key: useRef(null), providerKey: useRef(null) };

    if (!isInitialized) return null;
    if (isAuthenticated && user) return <Navigate to={homeFor(user.role)} replace />;

    const email = v.email.trim();
    const pw = v.password;
    const rules = RULES.map((r) => ({ label: r.label, ok: r.test(pw) }));
    // "I am a" always shows: the portal takes the role from a key an admin made and ignores this
    // choice for it, and the page does not guess which kind of key was typed (a beta key may have
    // the same shape). The provider key shows once the portal asks for it.
    const showProviderKey = askProviderKey && accountType === 'provider';
    const local = {
        name: !v.name.trim() ? 'Enter your name.' : '',
        email: !EMAIL_RE.test(email) ? 'Enter a valid email address.' : '',
        password: rules.every((r) => r.ok) ? '' : 'Password doesn’t meet the rules below.',
        confirm: !v.confirm || v.confirm !== pw ? 'Passwords don’t match.' : '',
        key: !v.key.trim() ? 'Enter your sign-up key.' : '',
        providerKey: showProviderKey && !v.providerKey.trim() ? 'Enter the provider key.' : '',
    };
    const errorOf = (k) => (tried ? local[k] : '') || server.field[k] || '';

    const set = (k) => (e) => {
        const value = e.target.value;
        setV((s) => ({ ...s, [k]: value }));
        if (server.field[k] || server.alert) setServer({ field: { ...server.field, [k]: '' }, alert: null });
    };

    async function handleSignUp(e) {
        e.preventDefault();
        if (busy) return;
        const firstBad = Object.keys(local).find((k) => local[k]);
        if (firstBad) {
            setTried(true);
            refs[firstBad].current?.focus();
            return;
        }
        setServer({ field: {}, alert: null });
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: v.name.trim(), email, password: pw, confirm: v.confirm, accountType, key: v.key.trim(),
                    ...(showProviderKey ? { providerKey: v.providerKey.trim() } : {}),
                }),
            });
            const data = await readJson(res);
            if (!res.ok) {
                const p = registerProblem(res.status, data);
                setServer({ field: p.field || {}, alert: p.alert || null });
                if (p.providerKey) setAskProviderKey(true);
                const bad = Object.keys(p.field || {})[0];
                // the provider key's field appears with this answer: focus it once it is there
                if (bad && !p.alert) setTimeout(() => refs[bad].current?.focus(), 0);
                setBusy(false);
                return;
            }
            // Registration doesn't send the verification email by itself — the original portal
            // asked for it straight after creating the account, so do the same here.
            let sent = false;
            try {
                const mail = await fetch(`${API_BASE}/auth/verify-email/send-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email }),
                });
                sent = mail.ok;
            } catch {
                sent = false;
            }
            navigate('/verify-email', { state: { email, sent, from: 'signup' } });
        } catch {
            setServer({ field: {}, alert: { title: 'We couldn’t reach enAble.', text: 'Check your connection and try again.' } });
            setBusy(false);
        }
    }

    const alert = server.alert;

    return (
        <AuthLayout>
            <AuthHead kicker="Get started" title="Create your account" />
            <AccountType value={accountType} onChange={setAccountType} />
            {alert ? (
                <AuthAlert
                    title={alert.title}
                    action={alert.verify ? (
                        <Link className="au-link" to="/verify-email" state={{ email, from: 'signup-existing' }}>Send a new link</Link>
                    ) : alert.login ? (
                        <Link className="au-link" to="/login">Log in</Link>
                    ) : null}
                >
                    {alert.text}
                </AuthAlert>
            ) : null}
            <form className="au-form" onSubmit={handleSignUp} noValidate>
                <AuthField label="Full name" type="text" name="name" autoComplete="name" value={v.name} onChange={set('name')} error={errorOf('name')} inputRef={refs.name} />
                <AuthField label="Email" type="email" name="email" autoComplete="email" inputMode="email" value={v.email} onChange={set('email')} error={errorOf('email')} inputRef={refs.email} />
                <AuthField
                    label="Password"
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    value={v.password}
                    onChange={set('password')}
                    error={errorOf('password')}
                    inputRef={refs.password}
                    describedBy="sp-rules"
                >
                    <ul id="sp-rules" className="sp-rules" aria-label="Password rules">
                        {rules.map((r) => (
                            <li key={r.label} className={r.ok ? 'sp-rule sp-rule--ok' : 'sp-rule'}>
                                <Icon name={r.ok ? 'check' : 'dash'} size={16} />
                                {r.label}
                                <span className="eg-sr-only">{r.ok ? ' (met)' : ' (not met yet)'}</span>
                            </li>
                        ))}
                    </ul>
                </AuthField>
                <AuthField label="Confirm password" type="password" name="confirm" autoComplete="new-password" value={v.confirm} onChange={set('confirm')} error={errorOf('confirm')} inputRef={refs.confirm} />
                <AuthField label="Sign-up key" type="text" name="key" autoComplete="off" autoCapitalize="none" spellCheck={false} value={v.key} onChange={set('key')} error={errorOf('key')} inputRef={refs.key} />
                {showProviderKey ? (
                    <AuthField label="Provider key" type="text" name="providerKey" autoComplete="off" autoCapitalize="none" spellCheck={false} value={v.providerKey} onChange={set('providerKey')} error={errorOf('providerKey')} inputRef={refs.providerKey} />
                ) : null}
                <Button type="submit" variant="primary" size="lg" block aria-disabled={busy || undefined} className="sp-submit">
                    {busy ? 'Creating account…' : 'Create account'}
                </Button>
            </form>
            <div className="au-switch">
                <span className="au-switch__text">Already have an account?</span>
                <Link className="au-link" to="/login">Log in</Link>
            </div>
        </AuthLayout>
    );
}

export default SignUpPage;
